import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { replaceWithComboItem } from '../reducers/menuSlice';
import { cartActions } from '../reducers/cartSlice';
import { analyzeItemConversion } from '../utils/itemConversion';
import { analyzeOptionReplacement } from '../utils/optionReplacement';
import { ParsedModifierGroup } from '../types/menu';
import { CartItem } from '../utils/cart';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { selectFeatureFlagItemConversion } from '../redux/features/config/config.selector';
import { selectItemConversionBusinessRules } from '../selectors/menu';
import { IBusinessRule } from '../types/brandMenu';

/**
 * Hook for handling both item conversion and option replacement
 */
const useItemConversion = () => {
  const dispatch = useDispatch();
  const isItemConversionEnabled = useSelector(selectFeatureFlagItemConversion);
  const {
    comboConversion,
    aLaCarteConversion,
    defaultSizeSelection,
    comboSizeTransformation,
  } = useSelector(selectItemConversionBusinessRules);

  // Extract combo conversion business rules from brand menu
  const itemConversionRules: IBusinessRule[] = useMemo(
    () => [...(comboConversion || []), ...(aLaCarteConversion || [])],
    [comboConversion, aLaCarteConversion]
  );

  const handleItemConversion = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      const conversionInfo = analyzeItemConversion(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        itemConversionRules
      );

      if (conversionInfo.shouldConvert) {
        dispatch(
          replaceWithComboItem({
            originalCartItem: currentCartItem,
            targetItemName: conversionInfo.targetItemId!,
            comboSize: conversionInfo.conversionSize,
            optionsToPreSelect: conversionInfo.optionsToPreSelect,
          })
        );
      }
    },
    [dispatch, itemConversionRules]
  );

  // Extract option replacement business rules (default size selection + combo size transformation)
  const optionReplacementRules: IBusinessRule[] = useMemo(
    () => [...(defaultSizeSelection || []), ...(comboSizeTransformation || [])],
    [defaultSizeSelection, comboSizeTransformation]
  );

  const handleOptionReplacement = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      const replacementInfo = analyzeOptionReplacement(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        optionReplacementRules
      );

      if (replacementInfo.shouldApplyReplacement) {
        // Apply option replacement by dispatching the new action
        dispatch(
          cartActions.applyOptionReplacement({
            cartItemId: currentCartItem.cartItemId,
            targetModifierGroups: replacementInfo.targetModifierGroups,
          })
        );
      }
    },
    [dispatch, optionReplacementRules]
  );

  const handleModifierSelection = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      // Check feature flags before running item conversion or option replacement
      // Both unified menu and item conversion feature flags must be enabled
      const isUnifiedMenu = getMenuUnificationFeatureFlag();
      if (!isUnifiedMenu || !isItemConversionEnabled) {
        return;
      }

      // First check for item conversion
      const conversionInfo = analyzeItemConversion(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        itemConversionRules
      );

      if (conversionInfo.shouldConvert) {
        // If conversion happens, let the thunk handle option replacement
        dispatch(
          replaceWithComboItem({
            originalCartItem: currentCartItem,
            targetItemName: conversionInfo.targetItemId!,
            comboSize: conversionInfo.conversionSize,
            optionsToPreSelect: conversionInfo.optionsToPreSelect,
          })
        );
      } else {
        // Only check for option replacement if no conversion happens
        handleOptionReplacement(
          modGroup,
          selectedMenuItemName,
          currentCartItem
        );
      }
    },
    [
      dispatch,
      handleOptionReplacement,
      isItemConversionEnabled,
      itemConversionRules,
    ]
  );

  return {
    handleItemConversion,
    handleOptionReplacement,
    handleModifierSelection,
  };
};

export default useItemConversion;
